import { createTheme } from '@material-ui/core/styles'
import Config from 'config'

const {
  xs,
  sm,
  md,
  xm,
  lg,
  xl
} = Config.layout


const themeType = 'light'

export const colors = {
  light: {
    primary: '#7600D3',
    secondary: '#057D72',
    secondaryLight: '#00D6C2',
    textPrimary: '#262626',
    textSecondary: '#6F6F6F',
    background: '#F4F4F4',
    paper: '#FFF',
    disabled: 'rgba(38, 38, 38, 0.24)',
    divider: '#E0E0E0',
    error: '#DA1E28',
    warning: '#F1C21B',
    success: '#24A148',
    info: '#0043CE'
  },
  defaults: {
    white: '#FFF',
    error: '#F44336',
    warning: '#FF9800',
    success: '#4CAF50',
    info: '#2196F3',
    scrollbar: '#8888',
    scrollbarHover: '#9999',
    scrollbarBg: '#b7000000'
  }
}


const theme = createTheme({
  palette: {
    type: 'light',
    primary: {
      main: colors[themeType].primary
    },
    secondary: {
      main: colors[themeType].secondary,
      light: colors[themeType].secondaryLight
    },
    error: {
      main: colors[themeType].error
    },
    warning: {
      main: colors[themeType].warning
    },
    success: {
      main: colors[themeType].success
    },
    info: {
      main: colors[themeType].info
    },
    text: {
      primary: colors[themeType].textPrimary,
      secondary: colors[themeType].textSecondary,
      disabled: colors[themeType].disabled
    },
    background: {
      default: colors[themeType].background,
      paper: colors[themeType].paper
    },
    divider: colors[themeType].divider
  },
   breakpoints: {
    values: {
      xs,
      sm,
      md,
      xm,
      lg,
      xl,
    },
  },
  typography: {
    fontFamily: [
      '"Montserrat"',
      '"Open Sans"',
      'sans-serif'
    ].join(', '),
    h1: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 300,
      fontSize: 96,
      lineHeight: '117px',
      letterSpacing: '-1.5px'
    },
    h2: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 300,
      fontSize: 60,
      lineHeight: '73px',
      letterSpacing: '-0.5px'
    },
    h3: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 400,
      fontSize: 48,
      lineHeight: '59px',
      letterSpacing: 0
    },
    h4: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 400,
      fontSize: 34,
      lineHeight: '41px',
      letterSpacing: '0.25px'
    },
    h5: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 400,
      fontSize: 24,
      lineHeight: '29px',
      letterSpacing: 0
    },
    h6: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 600,
      fontSize: 20,
      lineHeight: '24px',
      letterSpacing: '0.15px'
    },
    subtitle1: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 600,
      fontSize: 16,
      lineHeight: '24px',
      letterSpacing: '0.15px'
    },
    subtitle2: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '21px',
      letterSpacing: '0.1px'
    },
    body1: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '24px',
      letterSpacing: '0.5px'
    },
    body2: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '21px',
      letterSpacing: '0.25px'
    },
    button: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '19px',
      letterSpacing: '0.75px'
    },
    caption: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 400,
      fontSize: 12,
      lineHeight: '16px',
      letterSpacing: '0.4px'
    },
    overline: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 400,
      fontSize: 10,
      lineHeight: '14px',
      letterSpacing: '1.5px'
    },
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        html: {
          fontSize: 14
        },
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          margin: 0,
          '& .error-message': {
            color: colors[themeType].error
          }
        },
        '.app-wrapper': {
          height: '100vh',
          display: 'flex'
        },
        section: {
          backgroundColor: colors[themeType].containerBackground,
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          padding: '50px 28px'
        },
        // HTML produced by RichTextField, in the editor and wherever it is displayed
        '.rich-text-content': {
          lineHeight: 1.6,
          wordBreak: 'break-word',
          '& p': {
            margin: '2px 0'
          },
          '& h1, & h2, & h3': {
            fontWeight: 600,
            lineHeight: 1.3,
            margin: '1em 0 0.25em'
          },
          '& h1': {
            fontSize: '1.875em'
          },
          '& h2': {
            fontSize: '1.5em'
          },
          '& h3': {
            fontSize: '1.25em'
          },
          '& > :first-child': {
            marginTop: 0
          },
          '& ul, & ol': {
            margin: '4px 0',
            paddingLeft: '1.6em'
          },
          '& li > p': {
            margin: 0
          },
          '& a': {
            color: colors[themeType].primary,
            textDecoration: 'underline'
          },
          '& blockquote': {
            margin: '8px 0',
            paddingLeft: 14,
            borderLeft: '3px solid currentColor'
          },
          '& code': {
            padding: '0.15em 0.35em',
            borderRadius: 4,
            backgroundColor: 'rgba(135, 131, 120, 0.15)',
            color: '#EB5757',
            fontFamily: 'SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: '0.85em'
          },
          '& pre': {
            margin: '8px 0',
            padding: '14px 16px',
            borderRadius: 4,
            backgroundColor: '#F7F6F3',
            overflowX: 'auto',
            '& code': {
              padding: 0,
              backgroundColor: 'transparent',
              color: 'inherit'
            }
          },
          '& hr': {
            margin: '16px 0',
            border: 0,
            borderTop: `1px solid ${colors[themeType].divider}`
          },
          '& figure.table': {
            margin: '8px 0',
            overflowX: 'auto'
          },
          '& table': {
            width: '100%',
            margin: '8px 0',
            borderCollapse: 'collapse',
            tableLayout: 'fixed'
          },
          '& td, & th': {
            position: 'relative',
            padding: '6px 8px',
            border: `1px solid ${colors[themeType].divider}`,
            verticalAlign: 'top',
            textAlign: 'left',
            '& > p': {
              margin: 0
            }
          },
          '& th': {
            backgroundColor: '#F7F6F3',
            fontWeight: 600
          },
          '& ul[data-type="taskList"], & ul.todo-list': {
            listStyle: 'none',
            paddingLeft: 2
          },
          '& img.rich-image': {
            display: 'block',
            maxWidth: '100%',
            height: 'auto',
            margin: '8px 0',
            borderRadius: 4
          },
          '& video.rich-video': {
            display: 'block',
            width: '100%',
            maxHeight: 480,
            margin: '8px 0',
            borderRadius: 4,
            backgroundColor: '#000'
          },
          '& audio.rich-audio': {
            display: 'block',
            width: '100%',
            margin: '8px 0'
          },
          '& div[data-youtube-video]': {
            margin: '8px 0',
            '& iframe': {
              display: 'block',
              width: '100%',
              height: 'auto',
              aspectRatio: '16 / 9',
              border: 0,
              borderRadius: 4
            }
          },
          '& a.rich-file': {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            margin: '4px 0',
            padding: '6px 8px',
            borderRadius: 4,
            color: 'inherit',
            textDecoration: 'none',
            '&:hover': {
              backgroundColor: 'rgba(55, 53, 47, 0.06)'
            },
            '& .rich-file-name': {
              textDecoration: 'underline',
              textDecorationColor: 'rgba(55, 53, 47, 0.3)'
            },
            '& .rich-file-size': {
              color: colors[themeType].textSecondary,
              fontSize: '0.85em'
            }
          },
          '& a.rich-bookmark': {
            display: 'flex',
            margin: '8px 0',
            border: `1px solid ${colors[themeType].divider}`,
            borderRadius: 4,
            overflow: 'hidden',
            color: 'inherit',
            textDecoration: 'none',
            '&:hover': {
              backgroundColor: 'rgba(55, 53, 47, 0.03)'
            },
            '& .rich-bookmark-body': {
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              flex: '4 1 180px',
              minWidth: 0,
              padding: '12px 14px'
            },
            '& .rich-bookmark-title, & .rich-bookmark-description, & .rich-bookmark-url': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            },
            '& .rich-bookmark-description, & .rich-bookmark-url': {
              color: colors[themeType].textSecondary,
              fontSize: '0.85em'
            },
            '& .rich-bookmark-description:empty': {
              display: 'none'
            },
            '& .rich-bookmark-url': {
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              '& img': {
                width: 16,
                height: 16,
                flex: '0 0 auto'
              }
            },
            '& .rich-bookmark-image': {
              flex: '1 1 120px',
              maxWidth: 240,
              '& img': {
                display: 'block',
                width: '100%',
                height: '100%',
                maxHeight: 120,
                objectFit: 'cover'
              }
            }
          },
          '& .mention': {
            padding: '0 2px',
            borderRadius: 3,
            color: colors[themeType].primary,
            backgroundColor: 'rgba(118, 0, 211, 0.08)',
            textDecoration: 'none',
            whiteSpace: 'nowrap'
          },
          '& a.mention::before': {
            content: '"↗ "',
            opacity: 0.6
          },
          '& ul[data-type="taskList"] li': {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            '& > label': {
              flex: '0 0 auto',
              marginTop: '0.25em',
              userSelect: 'none'
            },
            '& > div': {
              flex: '1 1 auto',
              minWidth: 0
            },
            '&[data-checked="true"] > div': {
              color: colors[themeType].textSecondary,
              textDecoration: 'line-through'
            }
          }
        },
        '*::-webkit-scrollbar': {
          backgroundColor: colors.defaults.scrollbarBg,
          overflowY: 'scroll',
          width: 8
        },
        // background of the scrollbar except button or resizer
        '*::-webkit-scrollbar-track': {
          backgroundColor: 'inherit',
          '&:hover': {
            backgroundColor: 'inherit'
          }
        },
        // scrollbar itself
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: colors.defaults.scrollbar,
          borderRadius: 12,
          '&:hover': {
            backgroundColor: colors.defaults.scrollbarHover
          }
        },
        // set button(top and bottom of the scrollbar)
        '*::-webkit-scrollbar-button': {
          display: 'none'
        },
        '.snackbar-box': {
          padding: '0.5em 1rem',
          borderRadius: 5,
          display: 'flex',
          alignItems: 'center',
          color: colors.defaults.white,
          '&.error': {
            backgroundColor: colors.defaults.error
          },
          '&.warning': {
            backgroundColor: colors.defaults.warning
          },
          '&.success': {
            backgroundColor: colors.defaults.success
          },
          '&.info': {
            backgroundColor: colors.defaults.info
          },
          '& svg': {
            marginRight: '0.5rem'
          }
        },
      },
    },
    MuiContainer: {
      maxWidthSm: {
        '@media (min-width: 600px)': {
          maxWidth: '475px'
        },
      }
    },
    MuiDivider: {
      root: {
        marginTop: 8,
        marginBottom: 8,
        height: '1px'
      }
    },
    MuiFormControl: {
      root: {
        marginBottom: 0
      }
    },
    MuiButton: {
      root: {
        paddingTop: 13.5,
        paddingBottom: 13.5,
        textTransform: 'none',
        borderRadius: 10,
        marginBottom: 16,
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '21px',
        letterSpacing: '0.25px'
      },
      text: {
        paddingTop: 13.5,
        paddingBottom: 13.5,
        paddingLeft: 16,
        paddingRight: 16,
        textTransform: 'none',
        borderRadius: 10,
        marginBottom: 16,
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '21px',
        letterSpacing: '0.25px'
      },
      outlined: {
        paddingTop: 13.5,
        paddingBottom: 13.5,
        textTransform: 'none',
        borderRadius: 10,
        marginBottom: 16,
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '21px',
        letterSpacing: '0.25px'
      }
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 8,
        marginTop: 0,
        marginBottom: 0
      },
      input: {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 13.5,
        paddingBottom: 13.5,
        fontFamily: '"Open Sans", sans-serif',
        fontWeight: 400,
        fontSize: 14,
        height: 21,
        lineHeight: '21px',
        letterSpacing: '0.1px'
      }
    },
    MuiAutocomplete: {
      inputRoot: {
        paddingTop: 4.5,
        paddingBottom: 4.5,
        '&[class*="MuiOutlinedInput-root"]': {
          paddingTop: 4.5,
          paddingBottom: 3.5
        }
      }
    },
    MuiFormHelperText: {
      contained: {
        marginLeft: 16,
        marginRight: 12
      }
    },
    MuiChip: {
      root: {
        borderRadius: 24,
        padding: '4px 8px',
        height: 24,
        backgroundColor: colors[themeType].background
      },
      label: {
        paddingLeft: 0,
        paddingRight: 0,
        fontFamily: '"Open Sans", sans-serif',
        fontWeight: 400,
        fontSize: 12,
        lineHeight: '16px',
        letterSpacing: '0.4px'
      },
      clickable: {
        color: colors[themeType].primary,
        '& .MuiChip-icon': {
          color: colors[themeType].primary,
          marginRight: 4,
          marginLeft: 0
        },
        '&:hover': {
          textDecoration: 'underline',
          backgroundColor: colors[themeType].background
        }
      },
      deleteIcon: {
        margin: '0 -4px 0 4px'
      }
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: '#262626',
        fontSize: 12,
        lineHeight: '16px',
        letterSpacing: '0.4px',
        fontWeight: 400
      }
    },
    MuiBadge: {
      colorSecondary: {
        backgroundColor: colors[themeType].secondaryLight
      }
    },
    MuiPaper: {
      rounded: {
        borderRadius: 8
      }
    },
    MuiTab: {
      wrapper: {
        fontFamily: '"Open Sans", sans-serif',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '19.07px',
        letterSpacing: '0.75px',
        textTransform: 'none'
      }
    }
  }
})

export default theme
